<template>
    <div>
        <!-- Inline -->
        <div class="row q-mb-md">
            <!-- Filters hide/show button -->
            <div class="col-6">
                <div class="row" style="justify-content: flex-start">
                    <div>
                        <q-btn
                            @click="showFilters()"
                            :text-color="hideFilters ? 'grey-5' : 'white'"
                            class="neutral-btn ctrl-button btn-compact"
                            :class="{ primary: !hideFilters }"
                            icon="filter_list"
                            :icon-right="
                                hideFilters
                                    ? 'keyboard_arrow_up'
                                    : 'keyboard_arrow_down'
                            "
                        />
                    </div>
                    <!-- Searchfield -->
                    <div class="q-ml-md" style="flex-grow: 1">
                        <q-input
                            color="grey-5"
                            v-model="searchTerm"
                            @input="applyFilters"
                            :placeholder="
                                getString('module.course.list.filter.label')
                            "
                        />
                    </div>
                </div>
            </div>
            <!-- Invite button -->
            <div class="col-6" style="justify-content: flex-end">
                <q-btn
                    v-if="$store.state.meStore.me.rights.includes('course.add')"
                    class="capitalize ctrl-button right"
                    color="ctrl-turquoise"
                    :label="getString('action.add')"
                    @click="$router.push('/course/add')"
                    icon-right="add"
                />
                <q-btn
                    v-if="
                        $store.state.meStore.me.rights.includes('course.delete')
                    "
                    class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
                    color="ctrl-turquoise"
                    :disabled="!rowSelection.length"
                    :label="
                        $store.state.stringsStore.strings.list.actions.label
                    "
                    :class="{ 'button-active': rowSelection.length }"
                >
                    <q-icon size="large" name="mdi-chevron-down" />
                    <i v-show="rowSelection.length" class="counter">{{
                        rowSelection.length
                    }}</i>
                    <!-- Direct child of target -->
                    <q-popover anchor="bottom middle" self="top middle">
                        <q-list link>
                            <q-item
                                :class="{ disabled: !rowSelection.length }"
                                class="toolbar-list-item"
                                @click.native="removeRows('courses')"
                            >
                                <q-icon
                                    color="positive"
                                    size="large"
                                    name="delete"
                                />
                                {{
                                    $store.state.stringsStore.strings.action
                                        .remove
                                }}
                            </q-item>
                        </q-list>
                    </q-popover>
                </q-btn>
            </div>
        </div>
        <!-- Filters block -->
        <transition name="fade">
            <div v-if="!hideFilters" class="table-block q-mb-md">
                <div class="row q-pa-md" style="justify-content: space-between">
                    <!-- Searchfield -->
                    <div class="col-6 q-pa-md">
                        <q-input
                            color="primary"
                            v-model="filters.title"
                            @input="applyFilters"
                            :float-label="getString('title')"
                        />
                    </div>
                    <!-- Searchfield -->
                    <div class="col-6 q-pa-md">
                        <q-input
                            color="primary"
                            v-model="filters.location"
                            @input="applyFilters"
                            :float-label="
                                getString('block.type.button.location')
                            "
                        />
                    </div>
                </div>
                <div class="row q-pa-md" style="justify-content: space-between">
                    <div class="q-pa-md" style="vertical-align: middle">
                        <q-btn
                            :disable="!activeFilters"
                            @click="resetFilters()"
                            class="capitalize ctrl-button"
                            :color="activeFilters ? 'primary' : 'grey-4'"
                            label="Reset"
                        />
                    </div>
                </div>
            </div>
        </transition>
        <table-overview
            :endpoint="endpoint"
            :responseFormat="'courses'"
            :noDataFormat="'courses'"
            :columns="columns"
            :permission="permission"
            :module="'course'"
            :selectable="true"
        >
        </table-overview>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import TableOverview from '@/components/overview/TableOverview.vue';
// Mixins
import filtersMixin from '@/mixins/tables/filtersMixin';
import getString from '@/helpers/GetStrings';

export default {
  mixins: [
    filtersMixin,
  ],
  components: {
    TableOverview,
  },
  data() {
    return {
      typeOptions: [
        {
          label: 'Message',
          value: 'message',
        },
        {
          label: 'Warning',
          value: 'warning',
        },
        {
          label: 'Error',
          value: 'error',
        }],
      sortOptions: [
        {
          label: 'Back-end',
          value: 'back-end',
        },
        {
          label: 'Front-end',
          value: 'front-end',
        },
      ],
      rowSelection: [],
      filters: {
        title: '',
        location: '',
        type: [],
        sort: [],
      },
      permission: 'course',
      endpoint: 'course',
      responseData: {
        single: 'course',
        multiple: 'courses',
      },
      module: 'course',
      searchTerm: '',
      selectable: true,
      isLoading: false,
      columns: [
        {
          name: '__slot:course-title',
          title: getString('title'),
          sortField: 'title',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:course-location',
          title: getString('block.type.button.location'),
          sortField: 'location',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s',
        },
        {
          name: 'type',
          title: 'Type',
          sortField: 'type',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s',
          callback: this.getType,
        },
        {
          name: 'sort',
          title: getString('module.course.details.sort'),
          sortField: 'sort',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s',
          callback: this.getSort,
        },
      ],
      hideFilters: true,
      preferences: {
        page: 1,
        perPage: 10,
        sortField: null,
        sortOrder: null,
      },
    };
  },
  methods: {
    getString,

    getType(type) {
      let string;
      const lang = this.$store.state.stringsStore.lang;
      switch (type) {
        case 'training':
          string = 'Training';
          break;
        case 'course':
          string = lang === 'nl' ? 'Opleiding' : 'Course';
          break;
        case 'workshop':
          string = 'Workshop';
          break;

        default:
          break;
      }
      return string;
    },

    getSort(sort) {
      let string;
      const lang = this.$store.state.stringsStore.lang;
      switch (sort) {
        case 'practical-theory':
          string = lang === 'nl' ? 'Theorie & practicum' : 'Theory & practical';
          break;
        case 'theory':
          string = lang === 'nl' ? 'Theorie' : 'Theory';
          break;
        case 'practical':
          string = lang === 'nl' ? 'Practicum' : 'Practical';
          break;
        case 'webinar':
          string = 'Webinar';
          break;

        default:
          break;
      }
      return string;
    },

    removeRows(key) {
      EventBus.$emit('row-selection:delete', key);
    },
  },

  mounted() {
    EventBus.$on('row-selection:update', (payload) => {
      this.rowSelection = payload;
    });
  },
  destroyed() {
    EventBus.$off('row-selection:update');
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
</style>
